import { useUploadQueue } from "@/hooks/useUploadQueue";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2, Upload, X, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

/**
 * Global upload status bar that shows at the bottom of the screen
 * when there are active or pending uploads. Visible across all pages.
 */
export function UploadStatusBar() {
  const { items, totalCount, doneCount, errorCount, retryFailed, clearCompleted, hasPending } = useUploadQueue();
  const [expanded, setExpanded] = useState(false);

  // Don't show if no items
  if (totalCount === 0) return null;

  const pendingCount = totalCount - doneCount - errorCount;
  const activeItems = items.filter(i => i.status === "uploading" || i.status === "compressing" || i.status === "saving");
  const overallProgress = totalCount > 0
    ? Math.round(items.reduce((sum, i) => sum + (i.status === "done" ? 100 : i.progress), 0) / totalCount)
    : 0;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border shadow-lg">
      {/* Summary bar — always visible */}
      <div
        className="flex items-center justify-between px-4 py-2 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {hasPending ? (
            <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
          ) : errorCount > 0 ? (
            <XCircle className="w-4 h-4 text-red-500" />
          ) : (
            <CheckCircle className="w-4 h-4 text-green-500" />
          )}
          <span className="text-sm font-medium">
            {hasPending
              ? `Uploading ${activeItems.length} of ${pendingCount + doneCount + errorCount} files... (${overallProgress}%)`
              : errorCount > 0
                ? `${doneCount} uploaded, ${errorCount} failed`
                : `${doneCount} files uploaded`}
          </span>
          {/* Mini progress bar */}
          {hasPending && (
            <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-500"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {errorCount > 0 && (
            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); retryFailed(); }} className="h-7 text-xs">
              <RotateCcw className="w-3 h-3 mr-1" /> Retry {errorCount}
            </Button>
          )}
          {!hasPending && (
            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); clearCompleted(); }} className="h-7 text-xs">
              <X className="w-3 h-3 mr-1" /> Dismiss
            </Button>
          )}
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </div>
      </div>

      {/* Expanded file list */}
      {expanded && (
        <div className="max-h-48 overflow-y-auto border-t border-border px-4 py-2 space-y-1.5">
          {items.map(item => (
            <div key={item.id} className="flex items-center gap-3 text-sm">
              {item.status === "done" ? (
                <CheckCircle className="w-3.5 h-3.5 text-green-500 shrink-0" />
              ) : item.status === "error" ? (
                <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
              ) : (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500 shrink-0" />
              )}
              <span className="truncate flex-1 text-xs">{item.fileName}</span>
              <span className="text-muted-foreground shrink-0 text-xs">
                {item.status === "done" ? "Done"
                  : item.status === "error" ? (item.error || "Failed")
                  : item.status === "queued" ? "Waiting..."
                  : item.status === "compressing" ? "Compressing..."
                  : item.status === "saving" ? "Saving..."
                  : `${item.progress}%`}
              </span>
              {item.status !== "done" && item.status !== "error" && item.status !== "queued" && (
                <div className="w-16 h-1 bg-muted rounded-full overflow-hidden shrink-0">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all duration-300"
                    style={{ width: `${item.progress}%` }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
